import { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { PH, Toggle } from '../components/shared';
import { useApp } from '../context/AppContext';
import * as api from '../services/api';

const card = { background: "rgba(255,255,255,.72)", border: "1px solid rgba(255,255,255,.9)", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)" };

const Section = ({ title, children, action }) => (
  <div style={{ ...card, padding: "24px 28px", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid rgba(0,0,0,.05)" }}>
      <div style={{ fontSize: 14, fontWeight: 720, color: "#0f172a" }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, type = "text", disabled = false }) => (
  <div>
    <label style={{ fontSize: 11.5, fontWeight: 650, color: "#64748b", display: "block", marginBottom: 5 }}>{label}</label>
    <input className="inp" type={type} placeholder={placeholder} value={value || ""} onChange={e => onChange(e.target.value)} disabled={disabled} style={{ width: "100%", boxSizing: "border-box", opacity: disabled ? 0.6 : 1 }} />
  </div>
);

const ComingSoon = ({ icon, text }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "50px 20px", textAlign: "center" }}>
    <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(100,116,139,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--t1)", marginBottom: 5, fontFamily: "var(--fd)" }}>Coming Soon</div>
    <div style={{ fontSize: 12, color: "var(--t3)", maxWidth: 280 }}>{text}</div>
  </div>
);

const TABS = [
  { id: "account", ico: "👤", lbl: "Account Settings" },
  { id: "team", ico: "👥", lbl: "Team Settings" },
  { id: "security", ico: "🔒", lbl: "Security" },
  { id: "integrations", ico: "🔗", lbl: "Integrations" },
  { id: "notifications", ico: "🔔", lbl: "Notifications" },
  { id: "billing", ico: "💰", lbl: "Billing & Subscription" },
  { id: "api", ico: "⚡", lbl: "API & Developers" },
  { id: "privacy", ico: "🛡️", lbl: "Data & Privacy" },
];

export const PageSettings = memo(({ initTab = "account" }) => {
  const { showToast, user, updateUser, logout } = useApp();
  const [tab, setTab] = useState(initTab);

  // Account form
  const [acct, setAcct] = useState({ name: "", phone: "", company_name: "", job_title: "" });
  const [acctLoading, setAcctLoading] = useState(false);
  useEffect(() => {
    if (user) setAcct({ name: user.name || "", phone: user.phone || "", company_name: user.company_name || "", job_title: user.job_title || "" });
  }, [user]);

  const saveAccount = async () => {
    setAcctLoading(true);
    try {
      const res = await api.updateEnterpriseAccount(acct);
      updateUser(res.data || acct);
      showToast("Profile updated successfully!");
    } catch (e) { showToast(e.message || "Failed to update.", "error"); }
    finally { setAcctLoading(false); }
  };

  // Security
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const savePw = async () => {
    if (!pw.old_password || !pw.new_password) { showToast("Please fill all fields.", "error"); return; }
    if (pw.new_password.length < 8) { showToast("Password must be at least 8 characters.", "error"); return; }
    if (pw.new_password !== pw.confirm) { showToast("Passwords do not match.", "error"); return; }
    setPwLoading(true);
    try {
      await api.changePassword(pw.old_password, pw.new_password);
      showToast("Password updated successfully!");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { showToast(e.message || "Failed to change password.", "error"); }
    finally { setPwLoading(false); }
  };

  // Billing
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  useEffect(() => {
    if (tab === "billing" && !sub) {
      setSubLoading(true);
      api.getSubscription()
        .then(res => setSub(res))
        .catch(() => {})
        .finally(() => setSubLoading(false));
    }
  }, [tab]);

  // Data export
  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      const [leadsRes] = await Promise.all([api.getLeads()]);
      const leads = Array.isArray(leadsRes?.data) ? leadsRes.data : (Array.isArray(leadsRes) ? leadsRes : []);

      // Build CSV with BOM for Excel
      const bom = "\uFEFF";
      const leadsHeaders = ["Name", "Email", "Phone", "Company", "Status", "Source", "AI Score", "Created"];
      const leadsRows = leads.map(l => [
        l.name || "", l.email || "", l.phone || "", l.company || "",
        l.status || "", l.source || "", l.ai_score || "", l.created_at || ""
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
      const leadsCsv = bom + [leadsHeaders.join(","), ...leadsRows].join("\n");

      // Download
      const blob = new Blob([leadsCsv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gotaps_leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Export downloaded successfully!");
    } catch (e) { showToast(e.message || "Export failed.", "error"); }
    finally { setExporting(false); }
  };

  // Delete account
  const [delPw, setDelPw] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [showDelConfirm, setShowDelConfirm] = useState(false);

  const handleDeleteClick = () => {
    if (!delPw) { showToast("Please enter your password.", "error"); return; }
    setShowDelConfirm(true);
  };

  const confirmDelete = async () => {
    setDelLoading(true);
    try {
      await api.deleteEnterpriseAccount(delPw);
      setShowDelConfirm(false);
      showToast("Account deactivated. It will be permanently deleted after 14 days.");
      setTimeout(() => logout(), 1500);
    } catch (e) {
      setShowDelConfirm(false);
      showToast(e.message || "Failed to delete account.", "error");
    } finally { setDelLoading(false); }
  };

  const formatDate = d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="pg fi">
      <PH eyebrow="Account" title="Settings" sub="Manage your account, team, and preferences" />
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

        {/* Sidebar nav */}
        <div style={{ width: 220, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3, position: "sticky", top: 80 }}>
          {TABS.map(({ id, ico, lbl }) => {
            const active = tab === id;
            return (
              <button key={id} onClick={() => setTab(id)} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", borderRadius: 11, border: "none",
                cursor: "pointer", fontFamily: "var(--f)",
                background: active ? "rgba(15,23,42,.92)" : "rgba(255,255,255,.65)",
                color: active ? "#fff" : "#64748b",
                fontSize: 13, fontWeight: active ? 660 : 480,
                boxShadow: active ? "0 4px 14px rgba(0,0,0,.18)" : "0 2px 8px rgba(0,0,0,.04)",
                borderLeft: active ? "3px solid #0891b2" : "3px solid transparent",
                transition: "all .16s", textAlign: "left", width: "100%",
              }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,.9)"; e.currentTarget.style.color = "#0f172a"; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,.65)"; e.currentTarget.style.color = "#64748b"; } }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{ico}</span>
                <span>{lbl}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: 680 }}>

          {/* ═══ ACCOUNT ═══ */}
          {tab === "account" && (
            <Section title="Profile Information" action={<button className="btn bp" style={{ fontSize: 12 }} disabled={acctLoading} onClick={saveAccount}>{acctLoading ? "Saving..." : "Save Changes"}</button>}>
              <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 20 }}>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg,#64748b,#334155)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "#fff", flexShrink: 0 }}>{user?.name?.[0]?.toUpperCase() || "U"}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{user?.name || "User"}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{user?.email || ""}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Full Name" placeholder="Full Name" value={acct.name} onChange={v => setAcct(a => ({ ...a, name: v }))} />
                <Field label="Job Title" placeholder="Job Title" value={acct.job_title} onChange={v => setAcct(a => ({ ...a, job_title: v }))} />
                <Field label="Email" placeholder="Email" value={user?.email || ""} onChange={() => {}} disabled />
                <Field label="Phone" placeholder="+1 (555) 000-0000" value={acct.phone} onChange={v => setAcct(a => ({ ...a, phone: v }))} />
                <Field label="Company Name" placeholder="Company Name" value={acct.company_name} onChange={v => setAcct(a => ({ ...a, company_name: v }))} />
              </div>
            </Section>
          )}

          {/* ═══ SECURITY ═══ */}
          {tab === "security" && (
            <Section title="Change Password">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field label="Current Password" placeholder="••••••••" type="password" value={pw.old_password} onChange={v => setPw(p => ({ ...p, old_password: v }))} />
                <Field label="New Password" placeholder="••••••••" type="password" value={pw.new_password} onChange={v => setPw(p => ({ ...p, new_password: v }))} />
                <Field label="Confirm New Password" placeholder="••••••••" type="password" value={pw.confirm} onChange={v => setPw(p => ({ ...p, confirm: v }))} />
                <button className="btn bp" style={{ alignSelf: "flex-start" }} disabled={pwLoading} onClick={savePw}>{pwLoading ? "Updating..." : "Update Password"}</button>
              </div>
            </Section>
          )}

          {/* ═══ BILLING ═══ */}
          {tab === "billing" && (<>
            {subLoading ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--t3)", fontSize: 13 }}>Loading subscription...</div>
            ) : sub?.subscription ? (
              <Section title="Current Plan">
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 12, background: "linear-gradient(135deg,rgba(15,23,42,.04),rgba(15,23,42,.08))", border: "1px solid rgba(0,0,0,.08)", marginBottom: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,#0f172a,#334155)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👑</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 730, color: "#0f172a" }}>{sub.subscription.enterprise_type || "Enterprise"} Plan</div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{sub.subscription.description || "Active subscription"}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 680, padding: "4px 12px", borderRadius: 20, background: sub.subscription.is_expired ? "rgba(220,38,38,.1)" : "rgba(5,150,105,.1)", color: sub.subscription.is_expired ? "#dc2626" : "#059669", border: `1px solid ${sub.subscription.is_expired ? "rgba(220,38,38,.2)" : "rgba(5,150,105,.2)"}` }}>
                    {sub.subscription.is_expired ? "Expired" : "Active"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(0,0,0,.03)", border: "1px solid rgba(0,0,0,.06)" }}>
                    <div style={{ fontSize: 10.5, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>Start Date</div>
                    <div style={{ fontSize: 13, fontWeight: 640, color: "#0f172a" }}>{formatDate(sub.subscription.start_date)}</div>
                  </div>
                  <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(0,0,0,.03)", border: "1px solid rgba(0,0,0,.06)" }}>
                    <div style={{ fontSize: 10.5, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>End Date</div>
                    <div style={{ fontSize: 13, fontWeight: 640, color: "#0f172a" }}>{formatDate(sub.subscription.end_date)}</div>
                  </div>
                </div>
                {sub.subscription.file && (
                  <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(0,0,0,.03)", border: "1px solid rgba(0,0,0,.06)" }}>
                    <span style={{ fontSize: 16 }}>📄</span>
                    <span style={{ fontSize: 12.5, color: "#0f172a", flex: 1 }}>Contract Document</span>
                    <a href={sub.subscription.file} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#0891b2", fontWeight: 600, textDecoration: "none" }}>View</a>
                  </div>
                )}
              </Section>
            ) : (
              <Section title="Subscription">
                <div style={{ textAlign: "center", padding: 30, color: "var(--t3)", fontSize: 13 }}>No active subscription found.</div>
              </Section>
            )}
          </>)}

          {/* ═══ DATA & PRIVACY ═══ */}
          {tab === "privacy" && (<>
            <Section title="Export Your Data">
              <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 16, lineHeight: 1.65 }}>Download a CSV export of your leads data.</div>
              <button className="btn bp" style={{ gap: 8, fontSize: 12 }} disabled={exporting} onClick={exportData}>
                {exporting ? "Exporting..." : "⬇ Export Leads Data"}
              </button>
            </Section>
            <Section title="Privacy Settings">
              <ComingSoon icon="🛡️" text="Privacy settings will be available here soon." />
            </Section>
            <Section title="Danger Zone">
              <div style={{ padding: "16px", borderRadius: 12, border: "1.5px solid rgba(220,38,38,.2)", background: "rgba(220,38,38,.03)" }}>
                <div style={{ fontSize: 13, fontWeight: 680, color: "#dc2626", marginBottom: 6 }}>Delete Account</div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Your account will be deactivated for 14 days, then permanently deleted with all data.</div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 650, color: "#dc2626", display: "block", marginBottom: 5 }}>Enter your password to confirm</label>
                    <input className="inp" type="password" placeholder="Your password" value={delPw} onChange={e => setDelPw(e.target.value)} style={{ width: "100%", boxSizing: "border-box", borderColor: "rgba(220,38,38,.2)" }} />
                  </div>
                  <button disabled={delLoading || !delPw} onClick={handleDeleteClick} style={{ padding: "9px 18px", borderRadius: 9, border: "1.5px solid rgba(220,38,38,.3)", background: delPw ? "rgba(220,38,38,.08)" : "transparent", color: "#dc2626", fontSize: 12.5, fontWeight: 660, cursor: delPw ? "pointer" : "not-allowed", fontFamily: "var(--f)", opacity: delPw ? 1 : 0.5, whiteSpace: "nowrap" }}>
                    Delete Account
                  </button>
                </div>
              </div>
            </Section>
          </>)}

          {/* ═══ DELETE CONFIRMATION MODAL (portalled to body) ═══ */}
          {showDelConfirm && createPortal(
            <div style={{ position:"fixed", inset:0, zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div onClick={()=>setShowDelConfirm(false)} style={{ position:"absolute", inset:0, background:"rgba(10,12,20,.55)", backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)" }} />
              <div style={{ position:"relative", width:"90%", maxWidth:460, background:"#fff", borderRadius:22, padding:"32px 36px", boxShadow:"0 32px 80px rgba(0,0,0,.25),0 8px 32px rgba(0,0,0,.12)" }}>
                <div style={{ width:60, height:60, borderRadius:16, background:"rgba(220,38,38,.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 16px" }}>🗑️</div>
                <div style={{ fontSize:20, fontWeight:720, color:"#dc2626", textAlign:"center", marginBottom:8, fontFamily:"var(--fd)" }}>Delete Your Account?</div>
                <div style={{ fontSize:13, color:"#475569", textAlign:"center", lineHeight:1.7, marginBottom:20 }}>
                  This will start a <b style={{color:"#dc2626"}}>14-day cooldown period</b>. You can reactivate during this time by logging in again. After 14 days, deletion is permanent.
                </div>
                <div style={{ padding:"14px 18px", background:"rgba(220,38,38,.04)", border:"1px solid rgba(220,38,38,.12)", borderRadius:12, marginBottom:24 }}>
                  <div style={{ fontSize:11.5, fontWeight:660, color:"#dc2626", marginBottom:8 }}>The following data will be permanently lost:</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 16px", fontSize:12, color:"#64748b" }}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> All leads & analytics</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> Team member profiles</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> NFC cards & campaigns</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> Subteams & settings</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> Files & documents</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{color:"#dc2626",fontSize:10}}>●</span> Subscription data</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={()=>setShowDelConfirm(false)} style={{ flex:1, padding:"11px 0", borderRadius:11, border:"1px solid rgba(0,0,0,.1)", background:"rgba(0,0,0,.03)", color:"#475569", fontSize:13.5, fontWeight:580, cursor:"pointer", fontFamily:"var(--f)" }}>
                    Cancel
                  </button>
                  <button disabled={delLoading} onClick={confirmDelete} style={{ flex:1, padding:"11px 0", borderRadius:11, border:"none", background:"linear-gradient(150deg,#dc2626,#b91c1c)", color:"#fff", fontSize:13.5, fontWeight:680, cursor:"pointer", fontFamily:"var(--f)", boxShadow:"0 4px 14px rgba(220,38,38,.3)", opacity:delLoading?0.6:1 }}>
                    {delLoading ? "Deleting..." : "Yes, Delete My Account"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* ═══ COMING SOON TABS ═══ */}
          {tab === "team" && <Section title="Team Settings"><ComingSoon icon="👥" text="Team settings and permissions management will be available here soon." /></Section>}
          {tab === "integrations" && <Section title="Integrations"><ComingSoon icon="🔗" text="Connect your favorite tools and services. Coming soon." /></Section>}
          {tab === "notifications" && <Section title="Notification Preferences"><ComingSoon icon="🔔" text="Configure notification preferences and alert channels. Coming soon." /></Section>}
          {tab === "api" && <Section title="API & Developers"><ComingSoon icon="⚡" text="API keys, webhooks, and developer tools will be available here soon." /></Section>}

        </div>
      </div>
    </div>
  );
});
